// src/services/quickReplyService.js
import { getApiConfig } from '../config/markets.js';

const API_URL = getApiConfig().apiUrl;

/**
 * Obtener todas las respuestas rápidas del usuario
 * @param {string} market - Filtrar por mercado (opcional)
 * @param {string} search - Buscar en shortcut, title o content (opcional)
 * @returns {Promise<Array>}
 */
export async function getQuickReplies(market = null, search = null) {
  const token = localStorage.getItem('token');

  let url = `${API_URL}/quick-replies`;
  const params = new URLSearchParams();

  if (market) params.append('market', market);
  if (search) params.append('search', search);

  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
  });

  if (!response.ok) {
    let errMsg = 'Error al obtener respuestas rápidas';
    try {
      const data = await response.json();
      errMsg = data.error || data.message || errMsg;
    } catch {
      // Ignorar si no viene JSON
    }
    throw new Error(errMsg);
  }

  return await response.json();
}

/**
 * Buscar respuesta rápida por shortcut
 * @param {string} shortcut
 * @param {string} market - Mercado (opcional)
 * @returns {Promise<Object>}
 */
export async function getQuickReplyByShortcut(shortcut, market = null) {
  const token = localStorage.getItem('token');

  let url = `${API_URL}/quick-replies/shortcut/${encodeURIComponent(shortcut)}`;
  if (market) {
    url += `?market=${market}`;
  }

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || data.message || 'Respuesta rápida no encontrada');
  }

  return await response.json();
}

/**
 * Crear nueva respuesta rápida
 * @param {Object} quickReply - { shortcut, title, content, market, isGlobal }
 * @returns {Promise<Object>}
 */
export async function createQuickReply(quickReply) {
  const token = localStorage.getItem('token');

  const response = await fetch(`${API_URL}/quick-replies`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(quickReply)
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || data.message || 'Error al crear respuesta rápida');
  }

  return await response.json();
}

/**
 * Actualizar respuesta rápida
 * @param {string} id
 * @param {Object} updates - Campos a actualizar
 * @returns {Promise<Object>}
 */
export async function updateQuickReply(id, updates) {
  const token = localStorage.getItem('token');

  const response = await fetch(`${API_URL}/quick-replies/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(updates)
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || data.message || 'Error al actualizar respuesta rápida');
  }

  return await response.json();
}

/**
 * Eliminar respuesta rápida
 * @param {string} id
 * @returns {Promise<Object>}
 */
export async function deleteQuickReply(id) {
  const token = localStorage.getItem('token');

  const response = await fetch(`${API_URL}/quick-replies/${id}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || data.message || 'Error al eliminar respuesta rápida');
  }

  return await response.json();
}

/**
 * Obtener estadísticas de uso
 * @param {string} market - Filtrar por mercado (opcional)
 * @returns {Promise<Object>}
 */
export async function getQuickReplyStats(market = null) {
  const token = localStorage.getItem('token');

  let url = `${API_URL}/quick-replies/stats`;
  if (market) {
    url += `?market=${market}`;
  }

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || data.message || 'Error al obtener estadísticas');
  }

  return await response.json();
}
